import {
    GLOBAL_AND_SB_RULES,
    GLOBAL_LOCK_RULES,
    editorActionBar,
    noAccess,
    prLocked,
} from './global.js';
import {
    freeItem,
    priceFromCandex,
    priceFromPunchOut,
    sourceCandex,
    sourceNestBuy,
    sourceNestBuyAny,
    sourceNsc,
    sourcePsl,
    sourcePunchOut,
    wayOfBuyingFo,
} from './sources.js';

/**
 * Field id to the rules that disable it.
 * A field is disabled when at least one rule returns a reason code.
 * GLOBAL_AND_SB_RULES includes the pending SB tab. GLOBAL_LOCK_RULES does not.
 * @type {Object<string, Array<function(Object): (string|false)>>}
 */
export const FIELD_RULES = {
    'header.companyCode': GLOBAL_AND_SB_RULES,
    'header.purchaseOrganization': GLOBAL_AND_SB_RULES,
    'header.purchasingGroup': GLOBAL_AND_SB_RULES,
    'header.plant': GLOBAL_AND_SB_RULES,
    'header.storageLocation': GLOBAL_AND_SB_RULES,
    'header.deliveryAddress': GLOBAL_AND_SB_RULES,
    'header.validityStart': GLOBAL_LOCK_RULES,
    'header.validityEnd': GLOBAL_LOCK_RULES,
    'header.requestedDeliveryDate': GLOBAL_LOCK_RULES,
    'header.paymentTerms': GLOBAL_AND_SB_RULES,
    'header.orderingAddress': GLOBAL_AND_SB_RULES,
    'header.invoicePartner': GLOBAL_AND_SB_RULES,
    'header.incotermsCode': GLOBAL_AND_SB_RULES,
    'header.incotermsDescription': GLOBAL_AND_SB_RULES,
    'header.goodsReceipt': GLOBAL_AND_SB_RULES,
    'header.grBasedInvoiceVerification': GLOBAL_AND_SB_RULES,
    'header.ers': GLOBAL_AND_SB_RULES,

    'generalDetails.projectTitle': GLOBAL_AND_SB_RULES,
    'generalDetails.wayOfBuying': GLOBAL_AND_SB_RULES,
    'generalDetails.spender': GLOBAL_AND_SB_RULES,
    'generalDetails.requester': GLOBAL_AND_SB_RULES,

    'accountDetails.splitAccountByMaterial': GLOBAL_AND_SB_RULES,
    'accountDetails.bulkSelect': GLOBAL_AND_SB_RULES,
    'accountLine.selectAll': GLOBAL_AND_SB_RULES,
    'accountLine.resetAllocation': GLOBAL_LOCK_RULES,
    'accountLine.allocatePercentage': GLOBAL_LOCK_RULES,
    'accountLine.allocateAmount': GLOBAL_LOCK_RULES,
    'accountLine.allocateQuantity': GLOBAL_LOCK_RULES,
    'accountLine.split': GLOBAL_AND_SB_RULES,
    'accountLine.select': GLOBAL_AND_SB_RULES,
    'accountLine.accountType': GLOBAL_AND_SB_RULES,
    'accountLine.wbs': GLOBAL_AND_SB_RULES,
    'accountLine.costCenter': GLOBAL_AND_SB_RULES,
    'accountLine.profitCenter': GLOBAL_AND_SB_RULES,
    'accountLine.glNumber': GLOBAL_AND_SB_RULES,
    'accountLine.allocation': GLOBAL_AND_SB_RULES,
    'accountLine.delete': GLOBAL_AND_SB_RULES,

    'attachments.optionalUpload': GLOBAL_LOCK_RULES,
    'attachments.optionalTable': GLOBAL_LOCK_RULES,
    'attachments.cbUpload': GLOBAL_LOCK_RULES,
    'attachments.cbException': GLOBAL_LOCK_RULES,
    'attachments.cbExceptionReason': GLOBAL_LOCK_RULES,
    'attachments.cbTable': GLOBAL_LOCK_RULES,
    'attachments.internalComments': GLOBAL_LOCK_RULES,

    'sendingOptions.sendToRequester': GLOBAL_LOCK_RULES,
    'sendingOptions.sendToSpender': GLOBAL_LOCK_RULES,
    'sendingOptions.supplier': GLOBAL_LOCK_RULES,
    'sendingOptions.supplierEmails': GLOBAL_LOCK_RULES,
    'sendingOptions.p2pSupplier': GLOBAL_LOCK_RULES,
    'sendingOptions.vmdSupplier': GLOBAL_LOCK_RULES,
    'sendingOptions.pslSupplier': GLOBAL_LOCK_RULES,
    'sendingOptions.sendToOther': GLOBAL_LOCK_RULES,
    'sendingOptions.otherRecipients': GLOBAL_LOCK_RULES,
    'sendingOptions.p2pOther': GLOBAL_LOCK_RULES,
    'sendingOptions.hidePrice': GLOBAL_LOCK_RULES,
    'sendingOptions.supplierComment': GLOBAL_LOCK_RULES,

    'lineItems.addTransportCost': GLOBAL_AND_SB_RULES,
    'lineItems.bulkSelect': GLOBAL_AND_SB_RULES,
    'lineItems.addMaterial': GLOBAL_AND_SB_RULES,
    'lineItem.select': GLOBAL_AND_SB_RULES,
    'lineItem.description': [
        ...GLOBAL_AND_SB_RULES,
        sourceNsc,
        sourcePunchOut,
        sourceNestBuy,
    ],
    'lineItem.quantity': [
        ...GLOBAL_AND_SB_RULES,
        wayOfBuyingFo,
        priceFromPunchOut,
        priceFromCandex,
        sourceNsc,
        sourceNestBuy,
    ],
    'lineItem.unit': [
        ...GLOBAL_AND_SB_RULES,
        freeItem,
        priceFromCandex,
        sourceNestBuy,
        sourcePsl,
        priceFromPunchOut,
        sourceNsc,
    ],
    'lineItem.price': [
        ...GLOBAL_AND_SB_RULES,
        freeItem,
        priceFromCandex,
        sourceNestBuy,
        sourcePsl,
        priceFromPunchOut,
        sourceNsc,
    ],
    // PSL standard-template prices leave currency editable.
    'lineItem.currency': [
        ...GLOBAL_AND_SB_RULES,
        freeItem,
        priceFromCandex,
        sourceNestBuy,
        priceFromPunchOut,
        sourceNsc,
    ],
    'lineItem.per': [
        ...GLOBAL_AND_SB_RULES,
        freeItem,
        priceFromCandex,
        sourceNestBuy,
        priceFromPunchOut,
        sourcePsl,
        sourceNsc,
    ],
    'lineItem.taxCode': GLOBAL_AND_SB_RULES,
    'lineItem.vendorMaterialNumber': [
        ...GLOBAL_AND_SB_RULES,
        sourceCandex,
        sourcePunchOut,
        sourceNestBuyAny,
    ],
    'lineItem.requestedDeliveryDate': [
        ...GLOBAL_LOCK_RULES,
        wayOfBuyingFo,
    ],
    'lineItem.freeOfCharge': GLOBAL_AND_SB_RULES,
    'lineItem.supplierComments': GLOBAL_LOCK_RULES,
    'lineItem.plant': GLOBAL_AND_SB_RULES,
    'lineItem.storageLocation': GLOBAL_AND_SB_RULES,
    'lineItem.ppmCostPlan': GLOBAL_AND_SB_RULES,
    'lineItem.deliveryAddress': GLOBAL_AND_SB_RULES,
    'lineItem.internalComments': GLOBAL_LOCK_RULES,
    'lineItem.rowActions': GLOBAL_AND_SB_RULES,
    'lineItem.copy': [
        sourceCandex,
        sourcePunchOut,
        sourceNestBuyAny,
    ],

    'transportCosts.conditionType': GLOBAL_AND_SB_RULES,
    'transportCosts.percentage': GLOBAL_AND_SB_RULES,
    'transportCosts.amount': GLOBAL_AND_SB_RULES,
    'transportCosts.perQuantity': GLOBAL_AND_SB_RULES,
    'transportCosts.unit': GLOBAL_AND_SB_RULES,
    'transportCosts.currency': GLOBAL_AND_SB_RULES,
    'transportCosts.deleteRow': GLOBAL_AND_SB_RULES,
    'transportCosts.addNew': GLOBAL_AND_SB_RULES,
    'transportCosts.currentSupplier': GLOBAL_AND_SB_RULES,
    'transportCosts.otherSupplier': GLOBAL_AND_SB_RULES,
    'transportCosts.confirm': GLOBAL_AND_SB_RULES,

    'actions.mtvPowerBIValidated': [prLocked, noAccess],
    'actions.reject': [prLocked],
    'actions.save': [prLocked],
    'actions.submitToRequester': [prLocked],
    'actions.withdrawn': [prLocked],
    'actions.submit': [prLocked],
    'actions.saveSubmitBar': [editorActionBar],
};
